import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMediaQuery } from "react-responsive";

import { dataFaq } from "@data/index";
import {
  QUERY_WIDTHS,
  TRANSACTION_FILE_EXTENSIONS,
  getBrowserVersion,
  reportProblemSchema,
} from "@utils/index";
import { FileModel, ReportProblemFormInterface } from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import { useUserReportProblem } from "@providers/index";
import { attachmentsService } from "@services/index";
import { useNotify } from "@hooks/index";
import {
  Drawer,
  DrawerHeader,
  XSvg,
  Form,
  FormRow,
  FormFile,
  FormInput,
  FormText,
  FileItem,
  Button,
  ArrowUpRightSvg,
} from "@components/index";

export const ReportProblem = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const phone = useUserStore(({ phone }) => phone);
  const businesses = useUserStore(({ businesses }) => businesses);
  const userId = useUserStore(({ userId }) => userId);
  const [browserVersion] = useState(getBrowserVersion);
  const [files, setFiles] = useState<FileModel[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const userBusinessTypes = useMemo(
    () => businesses.map(({ type }) => type),
    [businesses],
  );

  const { mutate: mutateReportProblem, isPending: isPendingReportProblem } =
    useUserReportProblem(
      () => {
        handleClose();
        notify("success", "Your message has been submitted.");
      },
      ({ message, fields }) => {
        Object.keys(fields || {}).forEach((key) =>
          setError(key as "phone" | "message" | "userId" | "browserVersion", {
            message: fields![key],
          }),
        );
        notify("error", message);
      },
    );

  useEffect(() => {
    setState({ isLoading: isPendingReportProblem });
  }, [isPendingReportProblem, setState]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    getValues,
    setError,
  } = useForm<ReportProblemFormInterface>({
    mode: "onBlur",
    disabled: isPendingReportProblem,
    resolver: yupResolver(reportProblemSchema),
  });

  useEffect(() => {
    if (show) {
      reset({ phone });
    }
  }, [show, phone, reset]);

  const handleClose = () => {
    setState({ isDrawerShown: false });
    reset();
    setFiles([]);
    onClose();
  };

  const handleSelectFiles = async (target: HTMLInputElement) => {
    setIsUploading(true);
    if (!target.files?.length) {
      setIsUploading(false);
      return;
    }

    const newFiles = await attachmentsService.getAttachments(target.files);

    if (typeof newFiles === "string") {
      notify("error", newFiles);
      setIsUploading(false);
      return;
    }

    const newFilesWithSizes: FileModel[] = newFiles.map((file, index) => ({
      ...file,
      size: target.files?.[index]?.size || 0,
    }));
    setFiles([...files, ...newFilesWithSizes]);
    clearErrors("files");
    setIsUploading(false);
  };

  const removeFile = (uuid: string) => {
    const newFiles = files.filter((file) => file.uuid !== uuid);
    setFiles(newFiles);
  };

  const onSubmit = () => {
    const data = getValues();
    mutateReportProblem({ ...data, files });
  };

  return (
    show && (
      <Drawer>
        <DrawerHeader
          onClose={handleClose}
          title={
            <div>
              <div className="text-300 font-bold text-black-100 md:text-600">
                Report Problem
              </div>
              {!isMobile && (
                <div className="w-[380px] pt-1 text-300 text-black-54">
                  Please check our FAQ section before submitting the form, as it
                  may already answer your question.
                </div>
              )}
            </div>
          }
        />
        {isMobile && (
          <div className="px-4 pt-8 text-black-54">
            Please check our FAQ section before submitting the form, as it may
            already answer your question.
          </div>
        )}
        <Form
          className="flex max-h-[calc(100dvh_-_122px)] flex-col justify-between overflow-y-auto px-4 text-black-100 md:max-h-[calc(100dvh_-_156px)] md:px-14"
          onSubmit={handleSubmit(onSubmit)}
        >
          <input
            type="hidden"
            value={userId}
            {...register<"userId">("userId")}
          />
          <input
            type="hidden"
            value={browserVersion}
            {...register<"browserVersion">("browserVersion")}
          />
          <div>
            <FormRow top={isMobile ? "medium" : "large"}>
              <FormInput
                {...register<"phone">("phone")}
                type="phone"
                label="Phone number"
                error={formState.errors.phone?.message}
                onChange={() => clearErrors("phone")}
              />
            </FormRow>
            <FormRow top={isMobile ? "small" : "large"}>
              <FormText
                {...register<"message">("message")}
                name="message"
                label=""
                placeholder="Please describe the Problem"
                error={formState.errors.message?.message}
                onChange={() => clearErrors("message")}
              />
            </FormRow>
            <FormRow top="small">
              <FormFile
                {...register<"files">("files")}
                name="files"
                label="Add attachments"
                dataTestid="input_reportProblemFiles"
                error={formState.errors.files?.message}
                accept={TRANSACTION_FILE_EXTENSIONS}
                uploading={isUploading}
                onChange={(event) => {
                  handleSelectFiles(event.target as HTMLInputElement);
                  event.preventDefault();
                }}
              />
            </FormRow>
            {!files.length ? null : (
              <div className="mt-4 flex max-w-[430px] flex-col gap-y-4">
                {files.map((file) => (
                  <FileItem key={file.uuid} file={file} onRemove={removeFile} />
                ))}
              </div>
            )}
            <div className="pt-6 md:pt-10">
              <div className="pb-5 text-300 font-bold text-black-100 md:text-400">
                FAQ's
              </div>
              <div className="max-h-[calc(100dvh_-_622px)] min-h-32 overflow-y-auto md:max-h-[calc(100dvh_-_715px)]">
                {dataFaq.map(({ category, businessTypes }) =>
                  !businessTypes?.length ||
                  userBusinessTypes.find((type) =>
                    businessTypes?.includes(type),
                  ) ? (
                    <a
                      key={category}
                      href={`/faq#${category}`}
                      target="_blank"
                      className="flex justify-between border-b border-b-black-16 py-4 text-200 font-normal *:text-black-54 last:border-0 md:border-b-2 md:border-b-black-8 md:text-300 md:font-bold"
                    >
                      {category}
                      <ArrowUpRightSvg />
                    </a>
                  ) : null,
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-y-4 border-t-0 border-t-black-8 pb-2 md:-mx-14 md:flex-row md:justify-between md:border-t md:px-14 md:pb-4 md:pt-4">
            <Button type="submit" title="Submit" height="small" />
            <Button
              variant="secondary"
              leftIcon={<XSvg />}
              title="Cancel"
              height="small"
              onClick={handleClose}
            />
          </div>
        </Form>
      </Drawer>
    )
  );
};
